import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { ensureTicketAudioDuration } from '../../domain/ticket-audio-duration';
import { groupTicketMessageAttachments } from '../../domain/ticket-message-attachments';
import {
  isOwnTicketMessage,
  isAudioOnlyTicketMessage,
  ticketMessageReadState,
  ticketMessageSenderName,
} from '../../domain/ticket-message-state';
import {
  TICKET_MESSAGE_PREVIEW_LENGTH,
  TicketMessage,
  TicketSenderRole,
} from '../../domain/ticket.models';

export interface TicketAttachmentOpenEvent {
  readonly path: string;
  readonly trigger?: HTMLElement;
}

@Component({
  selector: 'app-ticket-message-list',
  standalone: true,
  imports: [CommonModule, NgIcon],
  templateUrl: './ticket-message-list.component.html',
  styleUrl: './ticket-message-list.component.scss',
})
export class TicketMessageListComponent {
  readonly messages = input.required<readonly TicketMessage[]>();
  readonly senderRole = input.required<TicketSenderRole>();
  readonly currentSenderId = input<string | null>(null);
  readonly currentSenderName = input('');
  readonly mediaUrl = input.required<(path: string) => string>();
  readonly senderLabel = input<(role: TicketSenderRole) => string>(
    (role) => role,
  );
  readonly emptyLabel = input('No messages yet.');
  readonly sentLabel = input('Sent');
  readonly readLabel = input('Read');
  readonly seeMoreLabel = input('See more');
  readonly seeLessLabel = input('See less');
  readonly openLabel = input('Open');
  readonly downloadLabel = input('Download');

  readonly imageOpen = output<TicketAttachmentOpenEvent>();
  readonly documentOpen = output<TicketAttachmentOpenEvent>();
  readonly contentSizeChange = output<void>();
  readonly stickToBottomChange = output<boolean>();

  @ViewChild('list') private list?: ElementRef<HTMLDivElement>;
  readonly expandedMessageIds = signal<ReadonlySet<string>>(new Set());

  ownMessage(message: TicketMessage): boolean {
    return isOwnTicketMessage(
      message,
      this.senderRole(),
      this.currentSenderId(),
    );
  }
  readState(message: TicketMessage) {
    return ticketMessageReadState(
      message,
      this.senderRole(),
      this.currentSenderId(),
    );
  }
  displayedSender(message: TicketMessage): string {
    if (this.currentSenderName() && this.ownMessage(message)) {
      return this.currentSenderName();
    }
    return ticketMessageSenderName(message, this.senderLabel());
  }
  isAudioOnly(message: TicketMessage): boolean {
    return isAudioOnlyTicketMessage(message);
  }
  isLong(message: TicketMessage): boolean {
    return (message.text?.length ?? 0) > TICKET_MESSAGE_PREVIEW_LENGTH;
  }
  displayedText(message: TicketMessage): string {
    const text = message.text ?? '';
    if (!this.isLong(message) || this.expandedMessageIds().has(message.id)) {
      return text;
    }
    return `${text.slice(0, TICKET_MESSAGE_PREVIEW_LENGTH).trimEnd()}…`;
  }
  toggle(messageId: string): void {
    this.expandedMessageIds.update((current) => {
      const next = new Set(current);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }
  groupedAttachments(message: TicketMessage) {
    return groupTicketMessageAttachments(message.attachments);
  }
  fileName(path: string): string {
    return decodeURIComponent(
      path.split('?')[0].split('/').pop() || 'Attachment',
    );
  }
  ensureAudioDuration(event: Event): void {
    ensureTicketAudioDuration(event.currentTarget as HTMLAudioElement);
  }
  videoMetadataLoaded(): void {
    this.contentSizeChange.emit();
  }
  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    this.stickToBottomChange.emit(
      element.scrollHeight - element.scrollTop - element.clientHeight < 80,
    );
  }
  openImage(path: string, event: Event): void {
    event.preventDefault();
    this.imageOpen.emit({ path, trigger: this.eventElement(event) });
  }
  openDocument(path: string, event: Event): void {
    this.documentOpen.emit({ path, trigger: this.eventElement(event) });
  }
  scrollToBottom(): void {
    const element = this.list?.nativeElement;
    if (element) element.scrollTop = element.scrollHeight;
  }
  private eventElement(event: Event): HTMLElement | undefined {
    return event.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : undefined;
  }
}
